import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDown } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/1090680/pexels-photo-1090680.jpeg";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const marqueeItems = [
  "Tax Preparation", "Bookkeeping", "Audit & Assurance", "Business Advisory", "Payroll", "Est. Kilkenny, Ireland",
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "0%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col justify-between overflow-hidden">
      <div className="mx-auto grid w-full max-w-7xl flex-1 grid-cols-1 items-center gap-12 px-6 pt-32 pb-16 lg:grid-cols-12 lg:px-12 lg:pt-40">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="overline-label mb-8 flex items-center gap-4"
            data-testid="hero-overline"
          >
            <span className="inline-block h-px w-10 bg-gold" />
            Chartered Practice · Kilkenny, Ireland
          </motion.p>

          <h1 className="font-serif text-[13vw] font-medium leading-[0.92] tracking-tight text-navy sm:text-6xl lg:text-7xl xl:text-8xl" data-testid="hero-heading">
            <MaskedLine delay={0.25}>Clarity in</MaskedLine>
            <MaskedLine delay={0.4}>
              every <em className="italic text-gold">number.</em>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-8 max-w-md text-base leading-relaxed text-muted sm:text-lg"
            data-testid="hero-tagline"
          >
            AB &amp; Co Accountant — tax, audit, bookkeeping, payroll and advisory
            for small businesses, startups and individuals across Ireland.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-cta-consultation"
              onClick={() => document.querySelector("#contact-form")?.scrollIntoView({ behavior: "smooth" })}
              className="group relative overflow-hidden bg-navy px-8 py-4 font-sans text-sm font-semibold uppercase tracking-[0.18em] text-cream"
            >
              <span className="absolute inset-0 translate-y-full bg-gold transition-transform duration-500 ease-out group-hover:translate-y-0" />
              <span className="relative transition-colors duration-500 group-hover:text-navy">Book a Consultation</span>
            </button>
            <button
              data-testid="hero-cta-services"
              onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
              className="group flex items-center gap-2 font-sans text-sm font-semibold uppercase tracking-[0.18em] text-navy"
            >
              Our Services
              <ArrowDown size={16} className="text-gold transition-transform duration-300 group-hover:translate-y-1" />
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden lg:col-span-5 lg:block"
        >
          <div className="absolute -left-5 -top-5 h-full w-full border hairline-gold" aria-hidden="true" />
          <div className="relative aspect-[4/5] overflow-hidden">
            <motion.img
              src={HERO_IMG}
              alt="Gold fountain pen resting on financial documents"
              style={{ y: imgY }}
              className="h-[125%] w-full object-cover grayscale-[35%]"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-navy/15" />
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1 }}
            className="mt-5 font-serif text-lg italic text-navy/60"
          >
            Precision is a habit, not an act.
          </motion.p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="border-t hairline-gold py-5"
        data-testid="hero-marquee"
      >
        <Marquee speed={28} gradient={false} pauseOnHover>
          {marqueeItems.map((item) => (
            <span key={item} className="mx-10 flex items-center gap-10 font-serif text-xl italic tracking-wide text-navy/70">
              {item}
              <span className="inline-block h-1.5 w-1.5 rotate-45 bg-gold" />
            </span>
          ))}
        </Marquee>
      </motion.div>
    </section>
  );
}
